import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { displayWorkOrderNumber } from "../components/snowflakes/work-orders/presentation";
import { useOptionalWorkScope } from "../components/WorkScopeProvider";
import {
  type AssetId,
  type WorkOrderId,
  workOrderIdSchema,
} from "../lib/workIds";
import { attachProcedureToWorkOrder } from "../queries/useProcedureAttachment";

export type CreateWorkOrderDraft = {
  title: string;
  description: string;
  instructions: string;
  assetId: AssetId | null;
  workTypeCode: string;
  priorityCode: string;
  assignedUserIds: number[];
  assignedTeamIds: number[];
  basicStartDate: string | null;
  basicFinishDate: string | null;
  estimatedMinutes: number | null;
  vendorName: string | null;
};

function useSelectedScope() {
  const scope = useOptionalWorkScope();
  return {
    customerId: scope?.selectedCustomer?.id,
    facilityId: scope?.selectedFacility?.id,
  };
}

function requireScope(scope: ReturnType<typeof useSelectedScope>) {
  if (!scope.customerId || !scope.facilityId)
    throw new Error("A customer and facility must be selected.");
  return { customerId: scope.customerId, facilityId: scope.facilityId };
}

/**
 * Creates a Work Order with any number of assigned users and teams. Owns the
 * success and failure toasts and refreshes Work Order lists.
 */
export function useCreateWorkOrderMutation() {
  const scope = useSelectedScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      draft: CreateWorkOrderDraft,
    ): Promise<{ id: WorkOrderId; number: string }> => {
      const { customerId, facilityId } = requireScope(scope);
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"].$post({
        param: { customerId, facilityId },
        json: draft,
      });
      if (!response.ok) {
        const errorBody = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(errorBody?.error ?? "Unable to create Work Order.");
      }
      const result: unknown = await response.json();
      const body =
        typeof result === "object" && result !== null ? result : undefined;
      return {
        id: workOrderIdSchema.parse(body && "id" in body ? body.id : undefined),
        number:
          body && "number" in body && typeof body.number === "string"
            ? body.number
            : "",
      };
    },
    onSuccess: (result) => {
      toast.add(
        result.number
          ? `Work Order ${displayWorkOrderNumber(result.number)} created.`
          : "Work Order created.",
        { icon: true },
      );
      void queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
    onError: () => {
      toast.error("Unable to create Work Order. Try again.", { icon: true });
    },
  });
}

/**
 * Attaches a Procedure to a Work Order that was just created. A failure here
 * means the Work Order exists without its Procedure, so the toast says so.
 */
export function useAttachCreatedWorkOrderProcedureMutation() {
  const scope = useSelectedScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      workOrderId: WorkOrderId;
      procedureId: string;
    }) => {
      const { customerId, facilityId } = requireScope(scope);
      return attachProcedureToWorkOrder({ customerId, facilityId, ...input });
    },
    onSuccess: (_attachment, variables) => {
      toast.add("Procedure attached.", { icon: true });
      void queryClient.invalidateQueries({
        queryKey: [
          "work-order-procedure",
          scope.customerId,
          scope.facilityId,
          variables.workOrderId,
        ],
      });
    },
    onError: () => {
      toast.error(
        "Work Order created, but the procedure could not be attached.",
        { icon: true },
      );
    },
  });
}

/** True when a request failed because the caller lacks permission. */
export function isForbiddenError(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === 403
  );
}
